import React, { createContext, useContext, useRef, useCallback, useMemo, useEffect } from 'react';
import authFetch from '../utils/authFetch';

/**
 * DataCacheContext — Global in-memory cache for API data.
 *
 * Prevents re-fetching on tab switches by caching responses.
 * Uses stale-while-revalidate: returns cached data instantly,
 * then refreshes in the background if TTL has expired.
 */

const DataCacheContext = createContext(null);

const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

export const useDataCache = () => {
  const context = useContext(DataCacheContext);
  if (!context) throw new Error('useDataCache must be used within DataCacheProvider');
  return context;
};

export const DataCacheProvider = ({ children }) => {
  const cache = useRef(new Map());
  const inflight = useRef(new Map());

  // Listen for the global 'pcc:clear-cache' event (fired on login/logout)
  // to wipe every cached entry. Guarantees no stale previous-user data
  // can leak into a new session.
  useEffect(() => {
    const handler = () => {
      cache.current.clear();
      inflight.current.clear();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('pcc:clear-cache', handler);
      return () => window.removeEventListener('pcc:clear-cache', handler);
    }
  }, []);

  /**
   * Fetch with cache. Returns cached data instantly if available.
   * If TTL expired, returns stale data and refreshes in background.
   *
   * @param {string} key - Cache key (usually the API URL)
   * @param {Function} fetcher - Async function that returns data
   * @param {object} options - { ttl, forceRefresh }
   * @returns {{ data, loading, refresh }}
   */
  const getCachedData = useCallback((key) => {
    const entry = cache.current.get(key);
    if (!entry) return null;
    return entry;
  }, []);

  const setCachedData = useCallback((key, data, ttl = DEFAULT_TTL) => {
    cache.current.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }, []);

  const isFresh = useCallback((key) => {
    const entry = cache.current.get(key);
    if (!entry) return false;
    return Date.now() - entry.timestamp < entry.ttl;
  }, []);

  /**
   * Smart fetch: returns cached data if fresh, or fetches + caches.
   * Deduplicates concurrent requests to the same key.
   */
  const cachedFetch = useCallback(async (key, url, options = {}) => {
    const { ttl = DEFAULT_TTL, forceRefresh = false } = options;

    // Return fresh cache
    if (!forceRefresh && isFresh(key)) {
      return cache.current.get(key).data;
    }

    // Return stale data exists (caller gets it from getCachedData)
    // but also trigger background refresh

    // Deduplicate: if same request is already in flight, wait for it
    if (inflight.current.has(key)) {
      return inflight.current.get(key);
    }

    const promise = authFetch(url)
      .then(data => {
        setCachedData(key, data, ttl);
        inflight.current.delete(key);
        return data;
      })
      .catch(err => {
        inflight.current.delete(key);
        // Return stale data on error
        const stale = cache.current.get(key);
        if (stale) return stale.data;
        throw err;
      });

    inflight.current.set(key, promise);
    return promise;
  }, [isFresh, setCachedData]);

  /**
   * Batch fetch multiple endpoints. Returns cached data for fresh keys,
   * fetches only stale/missing keys.
   */
  const cachedFetchAll = useCallback(async (requests, options = {}) => {
    const { forceRefresh = false } = options;
    const results = {};
    const toFetch = [];

    for (const { key, url } of requests) {
      if (!forceRefresh && isFresh(key)) {
        results[key] = cache.current.get(key).data;
      } else {
        toFetch.push({ key, url });
      }
    }

    if (toFetch.length > 0) {
      const settled = await Promise.allSettled(
        toFetch.map(({ key, url }) => cachedFetch(key, url, { forceRefresh }))
      );
      toFetch.forEach(({ key }, i) => {
        if (settled[i].status === 'fulfilled') {
          results[key] = settled[i].value;
        } else {
          // Use stale data on failure
          const stale = cache.current.get(key);
          if (stale) results[key] = stale.data;
        }
      });
    }

    return results;
  }, [isFresh, cachedFetch]);

  /**
   * Invalidate cache for a key or key prefix
   */
  const invalidate = useCallback((keyOrPrefix) => {
    if (!keyOrPrefix) {
      cache.current.clear();
      return;
    }
    for (const key of cache.current.keys()) {
      if (key === keyOrPrefix || key.startsWith(keyOrPrefix)) {
        cache.current.delete(key);
      }
    }
  }, []);

  /**
   * Prefetch data in the background (fire-and-forget)
   */
  const prefetch = useCallback((requests) => {
    for (const { key, url } of requests) {
      if (!isFresh(key)) {
        cachedFetch(key, url).catch(() => {}); // silent
      }
    }
  }, [isFresh, cachedFetch]);

  const value = useMemo(() => ({
    getCachedData,
    setCachedData,
    isFresh,
    cachedFetch,
    cachedFetchAll,
    invalidate,
    prefetch,
  }), [getCachedData, setCachedData, isFresh, cachedFetch, cachedFetchAll, invalidate, prefetch]);

  return (
    <DataCacheContext.Provider value={value}>
      {children}
    </DataCacheContext.Provider>
  );
};

export default DataCacheContext;
